// Pure module (no app imports) so the self-check runs in node: npx tsx src/shared/version/nuevaVersion.selfcheck.ts

/** sessionStorage key with the last version this tab was told about: {version, ts}. */
export const AVISADA_KEY = 'fornitalia.versionAvisada';

/** How long the popup stays quiet after showing a version ("Más tarde"). */
export const SILENCIO_MS = 10 * 60_000;

export type Avisada = { version: string; ts: number };

/**
 * Whether to show "hay una versión nueva".
 *
 * - empty `desplegada` -> nothing published yet (or the query failed): no popup.
 * - empty `actual`     -> nothing to compare with: no popup.
 * - equal              -> up to date. Also covers a rollback back to this build.
 * - recently shown     -> already shown for that version less than SILENCIO_MS ago.
 *
 * The expiring silence covers both "Más tarde" and a reload that lands on the old bundle again
 * (warm CDN): sessionStorage survives the reload, so the popup doesn't loop, and since the silence
 * expires the tab doesn't stay quiet until the next deploy either.
 */
export function hayQueAvisar(desplegada: string | null | undefined, actual: string | null | undefined, avisada: Avisada | null, ahora = Date.now()): boolean {
  if (!desplegada || !actual || desplegada === actual) return false;
  if (avisada?.version === desplegada && ahora - avisada.ts < SILENCIO_MS) return false;
  return true;
}

/** Parses the stored {version, ts}. Null when missing, unparseable or malformed. */
export function parseAvisada(raw: string | null): Avisada | null {
  if (!raw) return null;
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch {
    return null;
  }
  const o = v as Partial<Avisada> | null;
  return typeof o?.version === 'string' && Number.isFinite(o?.ts) ? { version: o.version, ts: o.ts as number } : null;
}
